// Phone-first Assign Tag: 1) scan / type the pre-printed tag (or take the next free one), 2) Register Child card with
// camera photo and icon fields, 3) success screen with Print Tag / Assign Next.
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useIdTemple, fetchAndPrint } from "../components/IdentityKit";
import { QrScanner } from "../components/QrScanButton";
import { Users, User, Phone, MapPin, Landmark, Camera, Keyboard, ScanLine, X, CheckCircle2, Printer, Baby, CalendarDays, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const plusDays = (d, n) => { const x = new Date(d + "T00:00:00"); x.setDate(x.getDate() + n); return x.toLocaleDateString("en-CA"); };
const BLANK = { child_name: "", age: "", gender: "", father_name: "", mother_name: "", mobile: "", city: "", district: "", address: "", photo: "" };

function shrink(file, cb) {
  const rd = new FileReader();
  rd.onload = () => {
    const img = new Image();
    img.onload = () => {
      const w = 480, h = Math.round((img.height / img.width) * 480) || 480;
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h); cb(c.toDataURL("image/jpeg", 0.75));
    };
    img.src = rd.result;
  };
  rd.readAsDataURL(file);
}

function Field({ icon: Icon, label, children, hint }) {
  return (
    <div>
      <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-1.5">{label}</p>
      <div className="relative flex items-center rounded-2xl bg-slate-50 border border-slate-200 focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 transition">
        <Icon className="w-5 h-5 text-slate-400 absolute left-4 pointer-events-none" />
        {children}
        {hint && <span className="absolute right-4 text-xs font-semibold text-slate-400">{hint}</span>}
      </div>
    </div>
  );
}
const inputCls = "w-full h-14 bg-transparent pl-12 pr-16 text-base text-slate-800 placeholder:text-slate-400 outline-none rounded-2xl";

export default function IdentityAssignTag() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const { temples, templeId } = useIdTemple(user);
  const [step, setStep] = useState(params.get("tag") ? "form" : "tag"); // tag | form | done
  const [tagMode, setTagMode] = useState("idle"); // idle | scan | manual
  const [tag, setTag] = useState(params.get("tag") || "");
  const [typed, setTyped] = useState("");
  const [f, setF] = useState(BLANK);
  const [valid, setValid] = useState({ from: today(), to: plusDays(today(), 10) });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [ranges, setRanges] = useState([]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const isAdmin = ["SUPER_ADMIN", "TEMPLE_ADMIN"].includes(user.role); // only admins may use "next free tag"

  useEffect(() => { api.get("/identity/tag-ranges").then((r) => setRanges(r.data)).catch(() => {}); }, [step]);

  const applyTag = async (raw) => {
    const s = String(raw || "").trim();
    const serial = (s.split("|")[1] || s).trim().toLowerCase();
    if (!serial) return;
    try {
      const r = await api.get("/identity/verify", { params: { code: serial } });
      if (!r.data.unassigned) { toast.error(`Tag ${serial} is already given to ${r.data.record?.child_name || "a child"}`); setTagMode("idle"); return; }
      toast.success("Valid QR code! Fill in child details.");
      setTag(serial); setStep("form");
    } catch (e) { toast.error(fmtErr(e)); setTagMode("idle"); }
  };

  const ok = f.child_name.trim() && f.father_name.trim() && f.mobile.length === 10 && f.district.trim();
  const submit = async () => {
    if (!ok) { toast.error("Fill child name, father name, 10-digit mobile and district"); return; }
    setBusy(true);
    try {
      const r = await api.post("/identity/children", { ...f, tag_serial: tag, valid_from: valid.from, valid_to: valid.to, temple_id: templeId || user.temple_id });
      setSaved(r.data); setStep("done");
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const next = () => { setF(BLANK); setTag(""); setTyped(""); setSaved(null); setTagMode("idle"); setStep("tag"); };

  return (
    <div className="max-w-lg mx-auto -mx-1 sm:mx-auto pb-8" data-testid="assign-tag-page">
      <h1 className="font-heading text-3xl font-bold text-maroon-900">Assign Tag</h1>
      <p className="text-slate-500 mb-4">Scan the child's QR safety tag and register the details.</p>

      {step === "tag" && (
        <>
          <div className="space-y-3 mb-4">
            <button onClick={() => setTagMode("scan")} className="w-full h-14 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center gap-2 font-semibold text-slate-800 active:scale-[0.99]" data-testid="at-scan"><Camera className="w-5 h-5" /> Scan QR</button>
            <button onClick={() => setTagMode("manual")} className="w-full h-14 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center gap-2 font-semibold text-slate-800 active:scale-[0.99]" data-testid="at-manual"><Keyboard className="w-5 h-5" /> Manual Entry</button>
          </div>
          <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5 text-center">
            {tagMode === "scan" ? (
              <>
                <QrScanner className="rounded-2xl min-h-[300px]" onScan={applyTag} onError={() => setTagMode("manual")} />
                <p className="text-sm text-slate-500 mt-3">Point the camera at the QR on the tag.</p>
                <button className="mt-2 text-sm font-semibold text-slate-600 underline" onClick={() => setTagMode("idle")}>Stop</button>
              </>
            ) : tagMode === "manual" ? (
              <div className="text-left">
                <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-1.5">Tag serial</p>
                <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyTag(typed)} placeholder="e.g. ssd0000001"
                  className="w-full h-14 rounded-2xl bg-slate-50 border border-slate-200 px-4 font-mono text-lg outline-none focus:border-sky-400 focus:ring-4 focus:ring-sky-100" data-testid="at-input" />
                <button onClick={() => applyTag(typed)} disabled={!typed.trim()} className="w-full h-14 mt-3 rounded-2xl bg-sky-500 text-white text-lg font-bold disabled:opacity-50" data-testid="at-use">Use this tag</button>
              </div>
            ) : (
              <>
                <div className="mx-auto w-20 h-20 rounded-full bg-sky-100 flex items-center justify-center"><ScanLine className="w-9 h-9 text-sky-600" /></div>
                <p className="font-bold text-xl text-slate-800 mt-4">Ready to Scan</p>
                <p className="text-slate-500">Tap below to start QR code scanning</p>
                <button onClick={() => setTagMode("scan")} className="mt-4 h-12 px-8 rounded-2xl bg-sky-500 text-white text-base font-semibold inline-flex items-center gap-2 shadow-lg shadow-sky-200" data-testid="at-start"><Camera className="w-5 h-5" /> Start Scanner</button>
                {isAdmin && <button onClick={() => { setTag(""); setStep("form"); }} className="block mx-auto mt-4 text-sm font-semibold text-sky-700 underline" data-testid="at-next-free">No pre-printed tag? Use the next free tag number</button>}
              </>
            )}
          </div>
          {ranges.length > 0 && (
            <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sky-800">
              <p className="font-semibold">Valid Tag Ranges</p>
              {ranges.map((x) => <p key={x.id} className="font-mono font-bold mt-1 text-sm">{x.first_tag.toUpperCase()} → {x.last_tag.toUpperCase()} <span className="font-sans font-normal text-sky-600">· {(x.capacity - x.used).toLocaleString("en-IN")} available</span></p>)}
            </div>
          )}
        </>
      )}

      {step === "form" && (
        <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5 space-y-5" data-testid="at-form">
          <div className="flex items-center gap-3">
            <span className="w-14 h-14 rounded-2xl bg-sky-100 flex items-center justify-center"><Users className="w-7 h-7 text-sky-600" /></span>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-xl text-slate-800">Register Child</p>
              <p className="text-slate-500">Tag: <span className="font-mono font-bold text-sky-600">{tag ? tag.toUpperCase() : "next free tag"}</span></p>
            </div>
            <button onClick={next} className="w-10 h-10 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100" aria-label="Cancel"><X className="w-6 h-6" /></button>
          </div>

          <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-center">
            <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500 flex items-center justify-center gap-1.5 mb-3"><Camera className="w-4 h-4" /> Child's photo</p>
            {f.photo && <img src={f.photo} alt="" className="mx-auto w-32 h-40 object-cover rounded-2xl border-4 border-white shadow mb-3" />}
            <label className="inline-flex items-center gap-2 h-12 px-6 rounded-2xl bg-amber-400 text-slate-900 font-bold shadow-md shadow-amber-200 cursor-pointer active:scale-[0.98]" data-testid="at-photo">
              <Camera className="w-5 h-5" /> {f.photo ? "Retake Photo" : "Open Camera"}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; if (file) shrink(file, (d) => set("photo", d)); }} />
            </label>
          </div>

          <Field icon={User} label="Child's name"><input className={inputCls} value={f.child_name} onChange={(e) => set("child_name", e.target.value)} placeholder="Enter child's full name" data-testid="at-child" /></Field>
          <div className="grid grid-cols-[1fr_1.4fr] gap-3">
            <Field icon={Baby} label="Age"><input className={`${inputCls} pr-3`} type="number" inputMode="numeric" min={0} max={18} value={f.age} onChange={(e) => set("age", e.target.value)} placeholder="Years" /></Field>
            <div>
              <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-1.5">Gender</p>
              <div className="grid grid-cols-2 gap-2 h-14">
                {["Boy", "Girl"].map((g) => (
                  <button key={g} type="button" onClick={() => set("gender", f.gender === g ? "" : g)}
                    className={`rounded-2xl border text-base font-semibold transition ${f.gender === g ? (g === "Boy" ? "bg-sky-500 border-sky-500 text-white" : "bg-pink-500 border-pink-500 text-white") : "bg-slate-50 border-slate-200 text-slate-600"}`}>{g}</button>
                ))}
              </div>
            </div>
          </div>
          <Field icon={User} label="Father's name"><input className={inputCls} value={f.father_name} onChange={(e) => set("father_name", e.target.value)} placeholder="Enter father's full name" data-testid="at-father" /></Field>
          <Field icon={User} label="Mother's name"><input className={inputCls} value={f.mother_name} onChange={(e) => set("mother_name", e.target.value)} placeholder="Enter mother's name (optional)" /></Field>
          <Field icon={Phone} label="Mobile number (10 digits)" hint={`${f.mobile.length}/10`}>
            <input className={inputCls} type="tel" inputMode="numeric" value={f.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="Enter 10 digit mobile number" data-testid="at-mobile" />
          </Field>
          <Field icon={MapPin} label="City / Village"><input className={inputCls} value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Enter city name" data-testid="at-city" /></Field>
          <Field icon={Landmark} label="District"><input className={inputCls} value={f.district} onChange={(e) => set("district", e.target.value)} placeholder="Enter district" data-testid="at-district" /></Field>
          <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-3">
            <Field icon={CalendarDays} label="Valid from"><input className={`${inputCls} pr-3 text-sm`} type="date" value={valid.from} onChange={(e) => setValid({ ...valid, from: e.target.value })} /></Field>
            <Field icon={CalendarDays} label="Valid to"><input className={`${inputCls} pr-3 text-sm`} type="date" min={valid.from} value={valid.to} onChange={(e) => setValid({ ...valid, to: e.target.value })} /></Field>
          </div>

          <button onClick={submit} disabled={busy || !ok} className="w-full h-16 rounded-2xl bg-sky-500 text-white text-xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-sky-200 disabled:bg-sky-300 disabled:shadow-none active:scale-[0.99]" data-testid="at-submit">
            <CheckCircle2 className="w-6 h-6" /> {busy ? "Registering…" : "Register Child"}
          </button>
        </div>
      )}

      {step === "done" && saved && (
        <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6 text-center" data-testid="at-done">
          <div className="mx-auto w-20 h-20 rounded-full bg-green-100 flex items-center justify-center"><CheckCircle2 className="w-11 h-11 text-green-600" /></div>
          <p className="font-bold text-2xl text-slate-800 mt-3">Child registered</p>
          <p className="text-slate-500">{saved.child_name} · Tag <span className="font-mono font-bold text-sky-600">{String(saved.tag_serial).toUpperCase()}</span></p>
          <p className="text-slate-500 text-sm mt-1"><Sparkles className="w-4 h-4 inline text-amber-500" /> Parent mobile {saved.mobile}</p>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <button onClick={() => fetchAndPrint("children", [saved], temples)} className="h-14 rounded-2xl border border-slate-200 font-bold text-slate-700 flex items-center justify-center gap-2"><Printer className="w-5 h-5" /> Print Tag</button>
            <button onClick={next} className="h-14 rounded-2xl bg-sky-500 text-white font-bold flex items-center justify-center gap-2 shadow-lg shadow-sky-200" data-testid="at-again"><RefreshCw className="w-5 h-5" /> Assign Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
